import SLog from '@/shared/lib/SLog'
import { toast } from 'sonner'
import { validateFiles } from '@/shared/lib/utils/file-validation'
import type { XYPosition } from '@xyflow/react'
import { uploadAsset } from '@/entities/asset'
import { createNode, createNodeId, updateNodeCustomData } from '@/entities/flow'
import { addCanvasAsset, mapUploadResultToCanvasAsset, patchNodeDataCache, setNodeUploading } from '@/entities/node'
import {
  findNodeDefinitionByFile,
  getNodeDefinition,
} from '@/shared/lib/node-registry'
import {
  readImageFileNaturalSize,
  type ImageNaturalSize,
} from '@/shared/lib/utils/image-utils'
import {
  readVideoFileNaturalSize,
  type VideoNaturalSize,
} from '@/shared/lib/utils/video-utils'
import { estimateNodeSize, layoutAssets } from '@/shared/lib/asset-layout'

interface CreateUploadingMediaNodeFromFileOptions {
  file: File
  position: XYPosition
  projectId: string
  onProgress?: (percent: number) => void
}

interface CreateUploadingMediaNodesFromFilesOptions {
  files: File[]
  position: XYPosition
  projectId: string
  onUploadSettled?: () => void
}

interface CreateUploadingMediaNodesFromFilesResult {
  startedCount: number
  uploads: Array<Promise<MediaNodeUploadResult>>
}

interface MediaNodeUploadSuccess {
  nodeId: string
  type: string
}

type MediaNodeUploadResult = MediaNodeUploadSuccess | null

function getSupportedMediaNodeFiles(files: File[]) {
  return files.filter((file) => findNodeDefinitionByFile(file))
}

function isUploadAbortedError(error: unknown) {
  return error instanceof Error && error.message === 'upload aborted'
}

async function readFileDisplaySize(file: File) {
  const nodeDefinition = findNodeDefinitionByFile(file)
  if (!nodeDefinition) return null

  let imageSize: ImageNaturalSize | undefined
  const requiresImageSize = nodeDefinition.create?.fileAccepts?.some((accept) => {
    return accept.toLowerCase().trim().startsWith('image/')
  })

  if (requiresImageSize) {
    try {
      imageSize = await readImageFileNaturalSize(file)
    } catch {
      const name = file.name.toLowerCase()
      if (name.endsWith('.tif') || name.endsWith('.tiff') || name.endsWith('.psd') || name.endsWith('.dib')) {
        imageSize = { width: 512, height: 512 }
      } else {
        toast.error('图片加载异常，无法上传')
        return null
      }
    }
  }

  let videoSize: VideoNaturalSize | undefined
  const requiresVideoSize = nodeDefinition.create?.fileAccepts?.some((accept) => {
    return accept.toLowerCase().trim().startsWith('video/')
  })

  if (requiresVideoSize) {
    try {
      videoSize = await readVideoFileNaturalSize(file)
    } catch {
      toast.error('视频加载异常，无法上传')
      return null
    }
  }

  return {
    nodeDefinition,
    width: imageSize?.width || videoSize?.width || 0,
    height: imageSize?.height || videoSize?.height || 0,
  }
}

export async function createUploadingMediaNodeFromFile({
  file,
  position,
  projectId,
  onProgress,
}: CreateUploadingMediaNodeFromFileOptions): Promise<MediaNodeUploadResult> {
  const fileDisplaySize = await readFileDisplaySize(file)
  if (!fileDisplaySize) return null

  const nodeId = createNodeId()
  const { id: createdNodeId, type } = createNode({
    type: '',
    input: {
      kind: 'uploaded-file',
      file,
      width: fileDisplaySize.width,
      height: fileDisplaySize.height,
      uploadedUrl: '',
    },
    position,
    nodeId,
  })

  if (!createdNodeId) {
    toast.error('无法识别媒体类型')
    return null
  }

  // 类似 Node.js path.parse(file.name).name 的行为
  // 提取最后一个点之前的内容作为纯文件名，如果点在开头或没有点，则保留原名
  const lastDotIndex = file.name.lastIndexOf('.')
  const cleanTitle = lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name

  setNodeUploading(createdNodeId, true)
  updateNodeCustomData(createdNodeId, { title: cleanTitle })
  patchNodeDataCache(createdNodeId, type, { title: cleanTitle })

  try {
    const result = await uploadAsset(file, {
      projectId,
      nodeId: createdNodeId,
      onProgress: (_taskId, percent) => onProgress?.(percent)
    })
    // 新链路：上传成功后写入画布素材表，并把节点 assetRefs 覆盖为该素材。
    addCanvasAsset(mapUploadResultToCanvasAsset(result), createdNodeId)

    const patch = getNodeDefinition(type)?.updateData?.({
      nodeId: createdNodeId,
      source: {
        kind: 'uploaded-file',
        file,
        width: result.width,
        height: result.height,
        uploadedUrl: result.url,
      },
    })

    if (patch) {
      const patchWithTitle = { ...patch, title: cleanTitle }
      updateNodeCustomData(createdNodeId, patchWithTitle)
      patchNodeDataCache(createdNodeId, type, patchWithTitle)
    }

    SLog.logEvent('upload', 'suc');
    return { nodeId: createdNodeId, type: fileDisplaySize.nodeDefinition.type }
  } catch (error) {
    if (!isUploadAbortedError(error)) {
      toast.error(error instanceof Error ? error.message : '媒体上传失败')
      SLog.logEvent('upload', `fail msg-${error instanceof Error ? error.message : '未知错误'}`);
    }
    return null
  } finally {
    setNodeUploading(createdNodeId, false)
  }
}

export async function createUploadingMediaNodesFromFiles({
  files,
  position,
  projectId,
  onUploadSettled,
}: CreateUploadingMediaNodesFromFilesOptions): Promise<CreateUploadingMediaNodesFromFilesResult> {
  const validFiles = await validateFiles(files)
  if (validFiles.length === 0) {
    return { startedCount: 0, uploads: [] }
  }
  const supportedFiles = getSupportedMediaNodeFiles(validFiles)

  if (supportedFiles.length === 0) {
    toast.error('当前只支持图片、视频、音频文件')
    return { startedCount: 0, uploads: [] }
  }

  if (supportedFiles.length < validFiles.length) {
    toast.error(`已跳过 ${validFiles.length - supportedFiles.length} 个不支持的文件`)
  }

  const processUploads = async () => {
    const displaySizes = await Promise.all(supportedFiles.map(file => readFileDisplaySize(file)))

    const assetsForLayout = displaySizes.map((size) => {
      if (!size) return { width: 300, height: 300 }

      let width = size.width
      let height = size.height

      if (!width || !height) {
        // Construct a dummy source to get default size
        const defaultData = size.nodeDefinition.createData()
        const defaultSize = size.nodeDefinition.getSize?.(defaultData)
        width = defaultSize?.width || 300
        height = defaultSize?.height || 300
      }

      if (size.nodeDefinition.type === 'image' || size.nodeDefinition.type === 'video') {
        return estimateNodeSize({ width, height })
      }
      return { width, height }
    })

    const positions = layoutAssets(assetsForLayout, { origin: position, gap: 48 })

    supportedFiles.forEach((file, index) => {
      createUploadingMediaNodeFromFile({
        file,
        position: positions[index],
        projectId,
      }).finally(() => {
        onUploadSettled?.()
      })
    })
  }

  processUploads()

  return { startedCount: supportedFiles.length, uploads: [] }
}
